/** @file fillTile: puts an image (or nothing) in one located tile slot; slot.fill and slot.fillMany share it. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { FacetEntry, ImageSlot } from '@zilo/pose3d-format/types/facet';
import type { Warning } from '@zilo/pose3d-format/types/warning';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandContext } from '../context/commandContext';
import type { ImageMeta, PuppetDoc } from '../puppetDoc/puppetDoc';
import type { SlotFillItem } from '../types/puppetInputs';

const same = (a: readonly number[], b: readonly number[]): boolean => a[0] === b[0] && a[1] === b[1];

function imageOf(ctx: CommandContext, draft: PuppetDoc, id: string, path: string): ImageMeta {
  return draft.images[id] ?? ctx.images[id] ?? throwError(ERROR_CODES.NOT_FOUND, { image: id }, { path, hint: 'upload the image first' });
}

/**
 * `item.image` into the located tile (`null` empties it); `at` is the item's input path (`/input` or
 * `/input/fills/<i>`). An image other than the entry's sheet image must have the tile's size, or `fit` (`pad` |
 * `scale`) adapts it with `IMAGE_RESIZED` and records `images[id].fit` (one fit per image id).
 */
export function fillTile(draft: PuppetDoc, ctx: CommandContext, located: { slot: ImageSlot; entry: FacetEntry }, item: SlotFillItem, at: string): Warning[] {
  const { slot, entry } = located;
  const tile = String(slot.tile);
  if (item.image === null) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- slots are keyed by tile index
    delete entry.slots[tile];
    return [];
  }
  const meta = imageOf(ctx, draft, item.image, `${at}/image`);
  const warnings: Warning[] = [];
  if (item.image !== draft.puppet.sheets[entry.sheet]?.image && !same(meta.size, slot.size)) {
    if (item.fit === undefined) {
      throwError(ERROR_CODES.VALIDATION, { expected: slot.size, actual: meta.size }, { path: `${at}/image`, hint: 'an image of the tile size, or fit: pad | scale' });
    }
    const shared = draft.images[item.image];
    if (shared !== undefined && shared.fit?.mode !== item.fit) {
      throwError(ERROR_CODES.VALIDATION, { image: item.image, fit: item.fit }, { path: `${at}/fit`, hint: 'the image is used elsewhere without this fit; upload a copy for this slot' });
    }
    draft.images[item.image] = { ...meta, fit: { mode: item.fit, from: [...meta.size] } };
    warnings.push({
      code: 'IMAGE_RESIZED', path: `/images/${item.image}`, params: { from: meta.size, to: slot.size, fit: item.fit },
      message: `image ${item.image} was adapted from ${meta.size.join('x')} to ${slot.size.join('x')} (${item.fit})`,
      hint: 'draw the tile at its slot size to avoid resampling',
    });
  }
  entry.slots[tile] = item.image;
  return warnings;
}
